/* Rol-onafhankelijke push-opt-in card. Toont status, aan/uit-toggle,
 * test-knop en foutmeldingen. Werkt binnen bestaande Card/Row-lay-outs.
 */
import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  isPushSupported, pushPermission,
  enablePush, disablePush, hasActiveSubscription, sendTestPush,
} from "@/lib/push";

export default function PushOptInCard({ compact = false }) {
  const [supported] = useState(isPushSupported());
  const [perm, setPerm] = useState(pushPermission());
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const has = await hasActiveSubscription();
      if (mounted) setActive(has);
    })();
    return () => { mounted = false; };
  }, []);

  const refresh = async () => {
    setPerm(pushPermission());
    setActive(await hasActiveSubscription());
  };

  const onEnable = async () => {
    setBusy(true);
    const r = await enablePush();
    setBusy(false);
    if (r.ok) { toast.success("Meldingen ingeschakeld"); }
    else if (r.reason === "denied") { toast.error("Toestemming geweigerd — pas dit aan in je browser."); }
    else if (r.reason === "unsupported") { toast.error("Meldingen niet ondersteund op dit apparaat."); }
    else { toast.error("Aanzetten mislukt — probeer opnieuw"); }
    await refresh();
  };
  const onDisable = async () => {
    setBusy(true);
    const r = await disablePush();
    setBusy(false);
    if (r.ok) toast.success("Meldingen uitgeschakeld");
    else toast.error("Uitschakelen mislukt");
    await refresh();
  };
  const onTest = async () => {
    setBusy(true);
    const r = await sendTestPush();
    setBusy(false);
    if (r.ok && (r.sent || 0) > 0) toast.success(`Test-melding verstuurd naar ${r.sent} apparaat(en)`);
    else if (r.ok) toast.info("Geen actieve apparaten — schakel eerst in");
    else toast.error("Test mislukt");
  };

  if (!supported) {
    return (
      <div className="p-4 flex items-center gap-3" data-testid="push-optin-unsupported">
        <BellOff className="h-5 w-5 text-neutral-400 shrink-0" />
        <div className="text-xs text-neutral-600">
          Meldingen worden niet ondersteund op dit apparaat/browser. Op iOS moet je de app eerst
          op je beginscherm zetten.
        </div>
      </div>
    );
  }

  const denied = perm === "denied";
  return (
    <div className={compact ? "p-3" : "p-4"} data-testid="push-optin-card">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-emerald-50 text-emerald-700" : "bg-[color:var(--sage-100)] text-[color:var(--sage)]"}`}>
          {active ? <CheckCircle2 className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-neutral-800 leading-tight">
            Meldingen op dit apparaat
          </div>
          <div className="text-[11px] text-neutral-500 leading-tight mt-0.5">
            {active
              ? "Actief — je ontvangt pushes voor belangrijke updates."
              : denied
                ? "Geweigerd. Zet meldingen aan via je browser-instellingen."
                : "Ontvang updates over je kind of rooster, ook als de app dicht is."}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {!active && !denied && (
              <Button
                size="sm"
                onClick={onEnable}
                disabled={busy}
                data-testid="push-enable-btn"
                className="bg-[color:var(--sage)] hover:bg-[color:var(--sage)]/90 text-white"
              >
                <Bell className="h-3.5 w-3.5 mr-1.5" /> Aanzetten
              </Button>
            )}
            {active && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onTest}
                  disabled={busy}
                  data-testid="push-test-btn"
                >
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Test-melding
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDisable}
                  disabled={busy}
                  data-testid="push-disable-btn"
                >
                  <BellOff className="h-3.5 w-3.5 mr-1.5" /> Uitzetten
                </Button>
              </>
            )}
            {denied && (
              <div className="flex items-center gap-1.5 text-[11px] text-amber-700">
                <AlertCircle className="h-3.5 w-3.5" />
                Aanpassen via browser-instellingen
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
